import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Box, Button, Dialog, Flex, Select, Text, TextField } from '@radix-ui/themes'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { z } from 'zod'
import { Ops } from '@willie/schemas'
import { useTRPC } from '~/utils/trpc'

export interface ContactSummary {
  id: string
  name: string
  email: string | null
  phone: string | null
  address: string | null
}

const contactFormSchema = Ops.Contacts.contactFieldsSchema.extend({
  orgId: Ops.Contacts.createContactInputSchema.shape.orgId
})

type ContactFormValues = z.infer<typeof contactFormSchema>

export interface ContactFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  // Locks the organization (e.g. when opened from an installation); when
  // omitted, creating a contact shows an organization selector.
  orgId?: string
  contact: ContactSummary | null
  onSaved?: (contactId: string) => void
}

const toFormValues = (contact: ContactSummary | null, orgId: string | undefined): ContactFormValues => ({
  orgId: orgId ?? '',
  name: contact?.name ?? '',
  email: contact?.email ?? null,
  phone: contact?.phone ?? null,
  address: contact?.address ?? null
})

interface ContactTextFieldProps {
  label: string
  name: 'name' | 'email' | 'phone' | 'address'
  control: ReturnType<typeof useForm<ContactFormValues>>['control']
  type?: 'text' | 'email'
}

const ContactTextField = ({ label, name, control, type = 'text' }: ContactTextFieldProps) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState: { error } }) => (
      <Box>
        <Text as="label" size="1" color="gray" className="block mb-1">
          {label}
        </Text>
        <TextField.Root
          type={type}
          value={field.value ?? ''}
          onBlur={field.onBlur}
          onChange={(e) => field.onChange(e.target.value || null)}
        />
        {error ? (
          <Text size="1" color="red" className="block mt-1">
            {error.message}
          </Text>
        ) : null}
      </Box>
    )}
  />
)

export const ContactFormDialog = ({ open, onOpenChange, orgId, contact, onSaved }: ContactFormDialogProps) => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const isEdit = contact !== null

  const orgsQuery = useQuery({
    ...trpc.ops.orgs.list.queryOptions(),
    enabled: open && !isEdit && !orgId
  })

  const { control, handleSubmit, reset } = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: toFormValues(contact, orgId)
  })

  useEffect(() => {
    if (open) reset(toFormValues(contact, orgId))
  }, [open, contact, orgId, reset])

  // Returned promise makes mutateAsync wait for the refetch, so a freshly
  // created contact is already in the pickers' list when the dialog closes.
  const invalidateContactConsumers = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.ops.contacts.list.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.list.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.get.queryKey() })
    ])

  const createMutation = useMutation(
    trpc.ops.contacts.create.mutationOptions({ onSuccess: invalidateContactConsumers })
  )
  const updateMutation = useMutation(
    trpc.ops.contacts.update.mutationOptions({ onSuccess: invalidateContactConsumers })
  )

  const pending = createMutation.isPending || updateMutation.isPending
  const errorMessage = createMutation.error?.message ?? updateMutation.error?.message ?? null

  const onSubmit = handleSubmit(async (values) => {
    try {
      const saved = isEdit
        ? await updateMutation.mutateAsync({
            contactId: contact.id,
            name: values.name,
            email: values.email,
            phone: values.phone,
            address: values.address
          })
        : await createMutation.mutateAsync(values)
      onSaved?.(saved.id)
      onOpenChange(false)
    } catch {
      // keep dialog open; the mutation error renders below the fields
    }
  })

  const handleOpenChange = (next: boolean) => {
    if (next) {
      createMutation.reset()
      updateMutation.reset()
    }
    onOpenChange(next)
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content maxWidth="480px">
        <Dialog.Title>{isEdit ? 'Edit contact' : 'New contact'}</Dialog.Title>
        <Dialog.Description size="2" color="gray">
          {isEdit
            ? 'Changes apply everywhere this contact is used.'
            : 'Contacts are reusable across installations of the same organization.'}
        </Dialog.Description>
        <Flex direction="column" gap="3" mt="4">
          {!isEdit && !orgId ? (
            <Controller
              name="orgId"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Box>
                  <Text as="label" size="1" color="gray" className="block mb-1">
                    Organization
                  </Text>
                  <Select.Root value={field.value || undefined} onValueChange={field.onChange}>
                    <Select.Trigger className="w-full" placeholder="Select an organization…" />
                    <Select.Content>
                      {(orgsQuery.data ?? []).map((org) => (
                        <Select.Item key={org.id} value={org.id}>
                          {org.name}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                  {error ? (
                    <Text size="1" color="red" className="block mt-1">
                      Organization is required
                    </Text>
                  ) : null}
                </Box>
              )}
            />
          ) : null}
          <ContactTextField label="Name" name="name" control={control} />
          <ContactTextField label="Email" name="email" control={control} type="email" />
          <ContactTextField label="Phone" name="phone" control={control} />
          <ContactTextField label="Address" name="address" control={control} />
        </Flex>
        {errorMessage ? (
          <Text color="red" size="2" as="p" mt="3">
            {errorMessage}
          </Text>
        ) : null}
        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray" disabled={pending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button onClick={onSubmit} loading={pending} disabled={pending}>
            {isEdit ? 'Save changes' : 'Create contact'}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
